import { useState } from "react";
import { Icon } from "@iconify/react";
import { SlideOver, Input } from "@/shared/components/dialog";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { Switch } from "@/shared/components/switch";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { inviteCaregiverApi } from "@/portals/patient/api/familyApi";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { CAREGIVER_CATEGORY_OPTIONS, type CaregiverAccess, type CaregiverCategoryValue } from "@/shared/types/family";
import { RequiredMark } from "@/shared/components/RequiredMark";

interface CategoryConfig {
  value: CaregiverCategoryValue;
  label: string;
  description: string;
  icon: string;
}

export const CATEGORY_ICONS: Record<CaregiverCategoryValue, string> = {
  Encounter: "lucide:stethoscope",
  Condition: "lucide:heart-pulse",
  MedicationRequest: "lucide:pill",
  AllergyIntolerance: "lucide:shield-alert",
  Observation: "lucide:activity",
  DiagnosticReport: "lucide:file-text",
  Procedure: "lucide:scissors",
  Immunization: "lucide:syringe",
};

const CATEGORIES: CategoryConfig[] = CAREGIVER_CATEGORY_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
  description: option.description,
  icon: CATEGORY_ICONS[option.value],
}));

type DurationMode = "end-date" | "no-expiration";

interface InviteCaregiverModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caregivers?: CaregiverAccess[];
  /** Called after the invite is actually created on the backend, so the caller can refetch. */
  onInvited: () => void;
}

function CategoryRow({ category, enabled, onToggle }: { category: CategoryConfig; enabled: boolean; onToggle: () => void }) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-l-4 px-4 py-3",
        enabled ? "border-[#F36F8F] bg-[#FFF0F3]" : "border-transparent",
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", enabled ? "bg-[#F36F8F]" : "bg-[#F3F4F6]")}>
          <Icon icon={category.icon} className={cn("h-4 w-4", enabled ? "text-white" : "text-[#6B7280]")} />
        </div>
        <div>
          <p className="text-sm font-bold text-[#0A2947]">{category.label}</p>
          <p className="text-xs text-[#9CA3AF]">{category.description}</p>
        </div>
      </div>
      <Switch checked={enabled} onCheckedChange={onToggle} />
    </div>
  );
}

export function InviteCaregiverModal({ open, onOpenChange, onInvited, caregivers = [] }: InviteCaregiverModalProps) {
  const [email, setEmail] = useState("");
  const [enabledCategories, setEnabledCategories] = useState<Record<string, boolean>>({});
  const [durationMode, setDurationMode] = useState<DurationMode>("no-expiration");
  const [expiresDate, setExpiresDate] = useState("");
  const [expirationCalendarOpen, setExpirationCalendarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedCategories = CATEGORIES.filter((category) => enabledCategories[category.value]);
  const allCategoriesSelected = CATEGORIES.every((category) => enabledCategories[category.value]);
  const toggleSelectAllCategories = () => {
    setEnabledCategories(
      allCategoriesSelected ? {} : Object.fromEntries(CATEGORIES.map((category) => [category.value, true])),
    );
  };
  const canSubmit =
    email.trim().length > 0 &&
    selectedCategories.length > 0 &&
    (durationMode === "no-expiration" || Boolean(expiresDate)) &&
    !isSubmitting;

  const resetForm = () => {
    setEmail("");
    setEnabledCategories({});
    setDurationMode("no-expiration");
    setExpiresDate("");
    setExpirationCalendarOpen(false);
  };

  const close = () => {
    onOpenChange(false);
    resetForm();
  };

  const handleSendInvite = async () => {
    if (!canSubmit) return;
    const trimmedEmail = email.trim();
    const resolvedExpiresDate = durationMode === "end-date" ? expiresDate : "";

    const existingCaregiver = caregivers.find((caregiver) => caregiver.email.toLowerCase() === trimmedEmail.toLowerCase());
    if (existingCaregiver?.status === "active") {
      toastError("This person is already your caregiver.");
      return;
    }
    if (existingCaregiver?.status === "pending") {
      toastError("You already have a pending caregiver invitation for this email.");
      return;
    }

    setIsSubmitting(true);
    const { error, code } = await inviteCaregiverApi({
      email: trimmedEmail,
      categories: selectedCategories.map((category) => category.value),
      expiresAt: resolvedExpiresDate ? new Date(`${resolvedExpiresDate}T23:59:59`).toISOString() : undefined,
    });
    setIsSubmitting(false);

    if (error) {
      const response = `${code ?? ""} ${error}`.toLowerCase();
      if (response.includes("active") || response.includes("accepted") || response.includes("connected")) {
        toastError("This person is already your caregiver.");
      } else if (response.includes("pending")) {
        toastError("You already have a pending caregiver invitation for this email.");
      } else if (response.includes("already") || response.includes("exist") || response.includes("relationship")) {
        toastError("This person is already your caregiver.");
      } else {
        toastError(error);
      }
      return;
    }

    // The backend sends the real invitation email itself (see
    // send_caregiver_invite_email in the backend) — nothing left to do
    // client-side once the API call above succeeds.
    onInvited();
    toastSuccess(`Invite sent to ${trimmedEmail}`);
    close();
  };

  return (
    <SlideOver open={open} onOpenChange={close} width={480}>
      <div className="p-7 pb-28">
        <div className="mb-1 flex items-center gap-1.5 pr-10">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E4002B]" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#E4002B]">Family &amp; Caregivers</span>
        </div>
        <h2 className="text-xl font-extrabold text-[#0A2947]">Invite a Caregiver</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Choose what they can see and for how long. You can change or revoke this anytime.</p>

        <div className="my-5 h-px bg-[#E5E7EB]" />

        <label className="mb-1.5 block text-xs font-semibold text-[#374151]">
          Caregiver's Email<RequiredMark />
        </label>
        <div className="msq-field-shell flex h-[42px] items-center gap-2.5 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-3 [&_:focus-visible]:!outline-none">
          <Icon icon="lucide:mail" className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
          <span className="h-4 w-px shrink-0 bg-[#E5E7EB]" />
          <Input
            variant="borderless"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@example.com"
            className="!h-full !w-full !bg-transparent !p-0 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF]"
          />
        </div>
        <p className="mt-2 flex items-start gap-1.5 text-xs text-[#9CA3AF]">
          <Icon icon="lucide:info" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          They'll receive an invite to create or link their MedSyncIQ account. Access begins only once they accept.
        </p>

        <div className="mt-5 overflow-hidden rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-start justify-between gap-3 border-b border-[#E5E7EB] px-4 py-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Record Categories</p>
              <p className="mt-0.5 text-xs text-[#6B7280]">Nothing is shared by default. You opt in to each type.</p>
            </div>
            <button
              type="button"
              onClick={toggleSelectAllCategories}
              className="shrink-0 text-xs font-bold text-[#E4002B] hover:underline"
            >
              {allCategoriesSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {CATEGORIES.map((category) => (
              <CategoryRow
                key={category.value}
                category={category}
                enabled={Boolean(enabledCategories[category.value])}
                onToggle={() =>
                  setEnabledCategories((current) => ({ ...current, [category.value]: !current[category.value] }))
                }
              />
            ))}
          </div>
          <div className="flex items-start gap-1.5 border-t border-[#E5E7EB] bg-[#F9FAFB] px-4 py-2.5 text-[11px] text-[#9CA3AF]">
            <Icon icon="lucide:lock" className="mt-0.5 h-3 w-3 shrink-0" />
            Access is read-only. Caregivers can never upload, edit, or delete records.
          </div>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2">
            <Icon icon="lucide:calendar" className="h-4 w-4 text-[#E4002B]" />
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Access Duration</p>
          </div>
          <p className="mb-3 text-xs text-[#6B7280]">Set separately from categories. This controls how long access lasts.</p>

          <div className="grid grid-cols-2 gap-3">
            <div
              onClick={() => {
                // Calendar day clicks bubble through React's portal back to this
                // card. While the picker is already open, do nothing; otherwise
                // this parent handler would reopen it immediately after selection.
                if (!expirationCalendarOpen) setExpirationCalendarOpen(true);
              }}
              className={cn(
                "cursor-pointer rounded-xl border p-3",
                durationMode === "end-date" ? "border-[#F36F8F] bg-[#FFF0F3]" : "border-[#E5E7EB]",
              )}
            >
              <div className="mb-1 flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
                    durationMode === "end-date" ? "border-[#F36F8F]" : "border-[#D1D5DB]",
                  )}
                >
                  {durationMode === "end-date" && <span className="h-2 w-2 rounded-full bg-[#F36F8F]" />}
                </span>
                <p className="text-xs font-bold text-[#0A2947]">Set an end date</p>
              </div>
              {durationMode === "end-date" ? (
                // A date has already been picked — show it in place, reopenable to change.
                <div onClick={(event) => event.stopPropagation()}>
                  <CalendarDateField
                    value={expiresDate}
                    onChange={(date) => setExpiresDate(date)}
                    disablePast
                    placeholder="Select expiration date"
                    open={expirationCalendarOpen}
                    onOpenChange={setExpirationCalendarOpen}
                  />
                </div>
              ) : expirationCalendarOpen ? (
                // Nothing picked yet — clicking the card opened the date-picker modal directly.
                // "Set an end date" only becomes the active choice once a date is actually chosen.
                <CalendarDateField
                  value=""
                  onChange={(date) => {
                    // Close the auto-expanded picker before switching branches.
                    // Otherwise the selected-date CalendarDateField mounts with the
                    // same controlled `open=true` value and immediately opens again.
                    setExpirationCalendarOpen(false);
                    setExpiresDate(date);
                    setDurationMode("end-date");
                  }}
                  disablePast
                  placeholder="Select expiration date"
                  open={expirationCalendarOpen}
                  onOpenChange={setExpirationCalendarOpen}
                  autoExpand
                />
              ) : (
                <p className="text-[11px] text-[#9CA3AF]">Access expires automatically.</p>
              )}
            </div>

            <div
              onClick={() => {
                setExpirationCalendarOpen(false);
                setDurationMode("no-expiration");
              }}
              className={cn(
                "cursor-pointer rounded-xl border p-3",
                durationMode === "no-expiration" ? "border-[#F36F8F] bg-[#FFF0F3]" : "border-[#E5E7EB]",
              )}
            >
              <div className="mb-1 flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
                    durationMode === "no-expiration" ? "border-[#F36F8F]" : "border-[#D1D5DB]",
                  )}
                >
                  {durationMode === "no-expiration" && <span className="h-2 w-2 rounded-full bg-[#F36F8F]" />}
                </span>
                <p className="text-xs font-bold text-[#0A2947]">No expiration</p>
              </div>
              <p className="text-[11px] text-[#9CA3AF]">Active until you revoke it manually.</p>
            </div>
          </div>
        </div>

        <div className="mt-5 flex min-h-[117px] items-center gap-5 rounded-[17px] border border-[#E4002B]/50 bg-transparent px-[22px] py-[17px]">
          <img src="/vector.png" alt="" aria-hidden="true" className="h-12 w-[29px] shrink-0 object-contain opacity-50" />
          <div className="min-w-0">
            <p className="text-xs font-extrabold uppercase tracking-wide text-[#120240]">What Caregiver Will Be Able To Do</p>
            <p className="mt-1 text-xs leading-[17px] text-[#9CA3AF]">
              View shared categories and read insights, nothing more. They can't upload, edit or delete anything, and can't
              choose which categories they see. They can request more categories at any time, which you'll need to approve.
            </p>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 flex justify-between gap-3 border-t border-[#E5E7EB] bg-white px-7 py-4">
        <Button
          variant="link"
          onClick={close}
          disabled={isSubmitting}
          className="!p-0 !text-sm !font-semibold !text-[#6B7280] hover:!text-[#0A2947]"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSendInvite}
          disabled={!canSubmit}
          loading={isSubmitting}
          className="!rounded-lg !border-transparent !bg-[#F27F95] !px-5 !py-2.5 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#EF667F] hover:!text-white"
        >
          {!isSubmitting && <Icon icon="lucide:send" className="h-4 w-4" />}
          Send Invite
        </Button>
      </div>
    </SlideOver>
  );
}
